import Lozenge from '@atlaskit/lozenge';
import Tooltip from '@atlaskit/tooltip';
import { Box, Heading } from '@fuegokit/react';
import moment from 'moment';
import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useJiraContext } from '../../screens/JiraContextProvider';
import { JiraUser, LastUserEntryDetails } from '../../types';
import { TogglTimeEntry } from '../../types/toggl-api';
import { isEllipsisActive } from '../../utils/utils';
import { formatTimestamp } from '../../views/configuration/my-entries/utils';
import { UserDetails } from './UserDetails';

export const Link = styled.a`
  color: var(--ds-link, #0052cc);
  text-decoration: none;
`;

export function EntryDetail({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  const value = children || '-';
  const el = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<ReactNode>();
  useEffect(() => {
    const content = isEllipsisActive(el.current) ? value : '';
    setTooltip(content);
  }, []);
  return (
    <Box>
      <Heading variant="h200">{label}</Heading>

      <div
        ref={el}
        style={{
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        <Tooltip
          tag="span"
          content={tooltip}
          delay={500}
          position="bottom-start"
        >
          <span style={{ fontSize: '13px' }}>{value}</span>
        </Tooltip>
      </div>
    </Box>
  );
}

function Duration({ entry }: { entry: TogglTimeEntry | null }) {
  if (!entry) return <>-</>;
  return (
    <>
      {entry.duration < 0 ? (
        <Lozenge appearance="inprogress" isBold>
          In Progress
        </Lozenge>
      ) : (
        formatTimestamp(entry.duration)
      )}
    </>
  );
}

export function LastEntryDetails({
  details,
  user,
}: {
  details: LastUserEntryDetails;
  user: JiraUser;
}) {
  const { baseUrl } = useJiraContext();
  const entry = details.timeEntry;
  const project = details.projectName;
  const workspace = details.workspaceName;
  const issueKey = details.issueKey;

  return (
    <UserDetails user={user}>
      <Box
        display="grid"
        gridRowGap="20px"
        gridTemplateColumns="minmax(0, 1fr)"
        gridColumnGap="4px"
      >
        <EntryDetail label="Last Jira entry">
          {issueKey && (
            <Link target="_blank" href={`${baseUrl}/browse/${issueKey}`}>
              {issueKey}
            </Link>
          )}
        </EntryDetail>
        <EntryDetail label="Latest entry name">
          {entry?.description}
        </EntryDetail>
        <Box
          display="grid"
          gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)"
          gridGap="20px"
        >
          <EntryDetail label="Started">
            {entry && moment(entry?.start).format('MM/DD/YYYY hh:mm A')}
          </EntryDetail>
          <EntryDetail label="Duration">
            <Duration entry={entry} />
          </EntryDetail>
          <EntryDetail label="Toggl workspace">{workspace}</EntryDetail>
          <EntryDetail label="Toggl project">{project}</EntryDetail>
        </Box>
      </Box>
    </UserDetails>
  );
}
